import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { LatLngTuple, LatLngBounds } from 'leaflet'; // Updated import
import TestComponent from './TestComponent';
import { ApiRequest } from 'GlobalFunctions';
import UsersList from './components/Users';
import ClientsList from './components/Clients';  // Add this import
import LoadingCircle from 'elements/LoadingCircle';
import { HiMapPin } from "react-icons/hi2";

import ReactDOMServer from 'react-dom/server';
import DateRange from 'elements/DateRange';
import moment from 'moment';
import { Divider } from '@mui/material';
import { HiFlag } from "react-icons/hi2";

// Define custom icon using React icons
const createReactIconMarker = (color = '#4285F4', isLastOrder = false) => {
    const iconHtml = ReactDOMServer.renderToString(
        <div style={{ color, fontSize: '32px' }}>
            {isLastOrder ? <HiFlag /> : <HiMapPin />}
        </div>
    );
    
    return L.divIcon({
        html: iconHtml,
        className: 'react-pin-icon',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
    });
};

// Component to fit bounds when markers are loaded
function MapBoundsHandler({ positions }: { positions: LatLngTuple[] }) {
    const map = useMap();
    
    useEffect(() => {
        if (positions.length > 0) {
            const bounds = L.latLngBounds(positions);
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [positions, map]);
    
    return null;
}

export default function Map() {
    const [userOrders, setUserOrders] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [validPositions, setValidPositions] = useState<LatLngTuple[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]); // Add this state
    const [formValues, setFormValues] = useState<any>({
        date: {
            startDate: moment().format("YYYY-MM-DD"),
            endDate: moment().format("YYYY-MM-DD")
        }
    });

    const getOrders = async () => {
        setIsLoading(true);
        await ApiRequest({
            url: '/purchases/orders-map/list',
            method: 'get',
            headers: {
                'ltkn': localStorage.getItem('ltkn')
            },
            query: {
                user_ids: selectedUserIds.length > 0 ? selectedUserIds : undefined,
                client_ids: selectedClientIds.length > 0 ? selectedClientIds : undefined,
                start_date: formValues.date.startDate,
                end_date: formValues.date.endDate
            },
            setResponse: (response: any) => {
                if (response.status === 'success') {
                    setUserOrders(response.data);
                } else {
                    console.error('Error in orders map response:', response.message);
                }
                setIsLoading(false);
            }
        });
    };

    useEffect(() => {
        getOrders();
    }, [selectedUserIds, selectedClientIds, formValues.date]); // Update dependency array

    const handleUserSelection = (userIds: string[]) => {
        setSelectedUserIds(userIds);
    };

    const handleClientSelection = (clientIds: string[]) => { // Add this handler
        setSelectedClientIds(clientIds);
    };

    // Default center if no orders - properly typed as LatLngTuple
    const defaultCenter: LatLngTuple = [4.7110, -74.0721];
    
    useEffect(() => {
        // Extract valid positions for map fitting
        const positions: LatLngTuple[] = [];
        userOrders.forEach(user => {
            user.orders.forEach((order: any) => {
                if (order.latitud && order.longitud) {
                    const lat = parseFloat(order.latitud);
                    const lng = parseFloat(order.longitud);
                    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                        positions.push([lat, lng]);
                    }
                }
            });
        });
        setValidPositions(positions);
    }, [userOrders]);
    
    // Prepare markers for all orders from all users
    const allMarkers = userOrders.flatMap(user => 
        user.orders.map((order: any, index: number) => {
            if (order.latitud && order.longitud) {
                const lat = parseFloat(order.latitud);
                const lng = parseFloat(order.longitud);
                
                // Validate coordinates
                if (isNaN(lat) || isNaN(lng)) {
                    console.error('Invalid coordinates', { lat: order.latitud, lng: order.longitud });
                    return null;
                }
                
                // Check if coordinates are in valid range
                if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                    console.error('Coordinates out of range', { lat, lng });
                    return null;
                }
                
                const position: LatLngTuple = [lat, lng];
                
                // Determine marker color based on order_type_id
                let markerColor = '#4285F4'; 
                if (order.order_type_id === 1) {
                    markerColor = '#4CAF50'; 
                } else if (order.order_type_id === 2) {
                    markerColor = '#e7a42a';
                } else if (order.order_type_id === 3) {
                    markerColor = '#cd3131'; 
                }
                
                // Check if this is the last order
                const isLastOrder = order.last_order === true;
                
                return (
                    <Marker 
                        key={`${user.user_id}-${order.order_id || index}`} 
                        position={position}
                        icon={createReactIconMarker(markerColor, isLastOrder)}
                    >
                        <Popup>
                            <div>
                                <h3>Vendedor: {user.user_name}</h3>
                                <p>Cliente: {order.client_name} {order.client_last_name}</p>
                                <p>Empresa: {order.business_name}</p>
                                <p>Fecha: {order.order_date}</p>
                                <p>Valor: ${Number(order.valor).toLocaleString()}</p>
                                <p>
                                    <div style={{ 
                                        display: 'inline-block', 
                                        backgroundColor: 
                                            order.order_type_id === 1 ? '#4CAF50' : 
                                            order.order_type_id === 2 ? '#e7a42a' : 
                                            order.order_type_id === 3 ? '#cd3131' : '#4285F4',
                                        color: 'white',
                                        padding: '3px 10px',
                                        borderRadius: '16px',
                                        fontSize: '0.85rem',
                                        fontWeight: 500
                                    }}>
                                        {order.order_type_id === 1 ? 'Efectivo' : 
                                         order.order_type_id === 2 ? 'Cotizacion' : 
                                         order.order_type_id === 3 ? 'Visita sin compra' : 'Desconocido'}
                                    </div>
                                </p>
                                {isLastOrder && <p><strong>Última orden</strong></p>}
                            </div>
                        </Popup>
                    </Marker>
                );
            }
            return null;
        }).filter(Boolean)
    );

    return (
        <div id="map_wrapper">
           
            <div className="map-container" style={{height: '100%'}}>
            <div className="map_top_bar">
                <DateRange
                    className="filters_datePicker"
                    label="Date range"
                    variant="outlined"
                    fullWidth
                    value={{
                        startDate: formValues.date?.startDate || null,
                        endDate: formValues.date?.endDate || null,
                    }}
                    format="MMMM DD, yyyy"
                    onChange={(e: any) => {
                        if (e.startDate && e.endDate) {
                            const startDate = moment(e.startDate).format("YYYY-MM-DD");
                            const endDate = moment(e.endDate).format("YYYY-MM-DD");

                            setFormValues({
                                ...formValues,
                                date: { startDate, endDate },
                            });
                        }
                    }}
                    variantDatePicker="side-by-side"
                />
            </div>
                {isLoading ? (
                    <LoadingCircle />
                ) : (
                    <MapContainer center={defaultCenter} zoom={13} scrollWheelZoom={false} className="map-container">
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {allMarkers}
                        {validPositions.length > 0 && <MapBoundsHandler positions={validPositions} />}
                    </MapContainer>
                )}
            </div>

            <div className="filters-wrapper">
                <UsersList onSelectUsers={handleUserSelection} />
                <Divider />
                {/* <ClientsList onSelectClients={handleClientSelection} /> */}
            </div>
        </div>
    )
}


